// src/components/tratamente/TratamenteList.jsx
import { useMemo, useState, useEffect } from "react";
import { useTratamente } from "../../context/TratamenteContext";
import { usePatients } from "../../context/PatientsContext";
import Button from "../ui/Button";
import {
  exportTratamentePDF,
  exportTratamenteQR,
  sendTratamenteEmail,
  exportProformaPDF,
  summarizePlanPayments,
  calculateToothPayment,
  getPaymentStatusText,
  formatCurrencyValue,
} from "../../services/exportTratamenteService";
import { getScopeKey, getScopeLabel, getScopeOrder, normalizeScope } from "../../utils/treatmentScope";

function TreatmentServiceRow({
  service,
  formatCurrencyValue,
  onToggle,
  onDelete,
  onSave,
  ToggleComponent,
}) {
  const [discount, setDiscount] = useState(service.discount ?? 0);
  const [finalPrice, setFinalPrice] = useState(service.finalPrice ?? service.basePrice ?? 0);

  useEffect(() => {
    setDiscount(service.discount ?? 0);
    setFinalPrice(service.finalPrice ?? service.basePrice ?? 0);
  }, [service.discount, service.finalPrice, service.basePrice]);

  const handleDiscountChange = (value) => {
    const parsed = Number(String(value).replace(",", "."));
    const nextDiscount = Number.isFinite(parsed) ? parsed : 0;
    setDiscount(nextDiscount);
    const newFinal = service.basePrice ? Math.round((service.basePrice * (100 - nextDiscount)) / 100) : 0;
    setFinalPrice(newFinal);
  };

  const handleFinalChange = (value) => {
    const parsed = Number(String(value).replace(",", "."));
    const nextFinal = Number.isFinite(parsed) ? parsed : 0;
    setFinalPrice(nextFinal);
    const newDisc = service.basePrice ? Math.round((1 - nextFinal / service.basePrice) * 100) : 0;
    setDiscount(newDisc);
  };

  const persistChanges = async () => {
    await onSave({
      discount,
      finalPrice,
    });
  };

  const serviceDone = Boolean(service.done);

  return (
    <tr className={serviceDone ? "border-t bg-gray-100 text-gray-500" : "border-t"}>
      <td className="p-2 align-middle">
        <ToggleComponent checked={serviceDone} onToggle={onToggle} />
      </td>
      <td className="p-2 align-middle">{service.name}</td>
      <td className="p-2 align-middle">{formatCurrencyValue(service.basePrice ?? 0)} RON</td>
      <td className="p-2 align-middle">
        <input
          type="text"
          inputMode="decimal"
          value={discount || ""}
          onChange={(e) => handleDiscountChange(e.target.value)}
          onBlur={persistChanges}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              persistChanges();
            }
          }}
          className="border rounded px-2 py-1 text-sm w-20"
          onClick={(e) => e.stopPropagation()}
        />
      </td>
      <td className="p-2 align-middle">
        <input
          type="text"
          inputMode="decimal"
          value={finalPrice || ""}
          onChange={(e) => handleFinalChange(e.target.value)}
          onBlur={persistChanges}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              persistChanges();
            }
          }}
          className="border rounded px-2 py-1 text-sm w-24 text-right"
          onClick={(e) => e.stopPropagation()}
        />
      </td>
      <td className="p-2 text-right align-middle">
        {!serviceDone && (
          <Button variant="danger" size="sm" onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}>
            🗑️ Șterge
          </Button>
        )}
      </td>
    </tr>
  );
}

export default function TratamenteList({
  patient: patientProp,
  onEditTooth,
  onAddToPlan,
  onFinancial,
}) {
  const ToggleBadge = ({
    checked,
    disabled,
    onToggle,
    stopPropagation = false,
    label = "",
  }) => {
    const baseClasses =
      "w-6 h-6 rounded border flex items-center justify-center text-xs font-semibold transition-colors";
    const stateClasses = checked
      ? "bg-green-500 border-green-500 text-white"
      : "bg-gray-100 border-gray-400 text-transparent";
    const disabledClasses = disabled
      ? "opacity-60 cursor-not-allowed"
      : "cursor-pointer hover:border-green-500 hover:text-white hover:bg-green-500";

    const handleClick = (event) => {
      if (stopPropagation) event.stopPropagation();
      if (disabled) return;
      onToggle();
    };

    return (
      <button
        type="button"
        className={`${baseClasses} ${stateClasses} ${disabledClasses}`}
        onClick={handleClick}
        aria-pressed={checked}
        aria-label={label}
        disabled={disabled}
      >
        ✓
      </button>
    );
  };

  const {
    tratamente,
    deleteTratament,
    toggleTratamentStatus,
    toggleTratamentServiceStatus,
    deleteTratamentService,
    updateTratament, // pentru discount/preț final
    registerPayment,
  } = useTratamente();

  const { pacienti, addFinancialRecord, syncPatient } = usePatients();

  const patientId = patientProp?.id;

  const effectivePatient = useMemo(() => {
    if (!patientId) return patientProp;
    const fromContext = Array.isArray(pacienti)
      ? pacienti.find((entry) => entry.id === patientId)
      : null;
    return fromContext || patientProp;
  }, [pacienti, patientId, patientProp]);

  const [collapsedDone, setCollapsedDone] = useState({});

  const pacientTratamenteRaw = useMemo(
    () => (patientId ? tratamente[patientId] || [] : []),
    [tratamente, patientId]
  );

  const pacientTratamente = useMemo(() => {
    const list = Array.isArray(pacientTratamenteRaw) ? pacientTratamenteRaw.slice() : [];
    list.sort((a, b) => {
      const scopeA = normalizeScope(a.scope, a.tooth);
      const scopeB = normalizeScope(b.scope, b.tooth);
      const orderA = getScopeOrder(scopeA, a.tooth);
      const orderB = getScopeOrder(scopeB, b.tooth);
      if (orderA !== orderB) return orderA - orderB;
      if (scopeA === "tooth" && scopeB === "tooth") {
        const numA = Number(a.tooth);
        const numB = Number(b.tooth);
        if (Number.isFinite(numA) && Number.isFinite(numB) && numA !== numB) {
          return numA - numB;
        }
      }
      return 0;
    });
    return list;
  }, [pacientTratamenteRaw]);

  const planSummary = useMemo(
    () => summarizePlanPayments(pacientTratamente),
    [pacientTratamente]
  );

  const totalGeneral = planSummary.totalGeneral;
  const totalPaidAmount = planSummary.totalPaid;
  const totalRemaining = planSummary.totalRemaining;
  const totalAdvance = planSummary.totalAdvance;
  const patientAdvanceBalance = Number(effectivePatient?.advanceBalance || 0);
  const [paymentInputs, setPaymentInputs] = useState({});

  useEffect(() => {
    setPaymentInputs({});
  }, [patientId]);

  const summaryById = useMemo(() => {
    const map = new Map();
    planSummary.entries.forEach((entry) => {
      if (entry.id != null) {
        map.set(entry.id, entry);
      }
    });
    return map;
  }, [planSummary]);

  const buildServiceKey = (service) => {
    const tooth = service?.tooth ?? service?.toothNumber ?? "";
    const scope = normalizeScope(service?.scope, tooth);
    const name = service?.name ?? "";
    const price = Number(
      service?.finalPrice ?? service?.price ?? service?.basePrice ?? 0
    ).toFixed(2);
    return `${getScopeKey(scope, tooth)}|${name}|${price}`;
  };

  const allPlanServices = useMemo(() => {
    const list = [];
    pacientTratamente.forEach((tratament) => {
      const tooth = tratament.tooth;
      const scope = normalizeScope(tratament.scope, tooth);
      (tratament.services || []).forEach((srv) => {
        list.push({
          ...srv,
          tooth,
          scope,
          serviceKey: buildServiceKey({ ...srv, tooth, scope }),
          basePrice: srv.basePrice ?? 0,
          finalPrice: srv.finalPrice ?? srv.basePrice ?? 0,
          discount: srv.discount ?? 0,
        });
      });
    });
    return list;
  }, [pacientTratamente]);

  if (!patientId) return null;

  const handleExportPdf = () =>
    exportTratamentePDF(effectivePatient, pacientTratamente, planSummary);

  const handleExportQr = async () => {
    const qrUrl = await exportTratamenteQR(
      effectivePatient,
      pacientTratamente,
      planSummary
    );
    const popup = window.open();
    if (popup) {
      popup.document.write(`<img src="${qrUrl}" alt="QR Code" style="max-width:100%;" />`);
    }
  };

  const handleSendEmail = () =>
    sendTratamenteEmail(effectivePatient, pacientTratamente, planSummary);

  const handleExportProforma = async () => {
    if (allPlanServices.length === 0) {
      alert("Nu există servicii în planul de tratament.");
      return;
    }
    const result = exportProformaPDF(effectivePatient, pacientTratamente, planSummary);
    if (result?.dataUri) {
      try {
        const totalPlanAmount = allPlanServices.reduce(
          (sum, srv) => sum + (Number(srv.finalPrice) || 0),
          0
        );
        await addFinancialRecord(patientId, {
          id: `fin-${Date.now()}`,
          createdAt: new Date().toISOString(),
          type: "plan",
          total: totalPlanAmount,
          services: allPlanServices.map((srv) => ({
            tooth: srv.tooth,
            scope: srv.scope || "",
            name: srv.name,
            basePrice: srv.basePrice ?? 0,
            discount: srv.discount ?? 0,
            finalPrice: srv.finalPrice ?? srv.basePrice ?? 0,
            serviceKey: srv.serviceKey || buildServiceKey(srv),
          })),
          fileName: result.fileName,
          pdfData: result.dataUri,
          isProforma: false,
        });
      } catch (err) {
        console.error("Eroare salvare proformă:", err);
      }
    }
  };

  const renderEmpty = () => (
    <div className="flex flex-col gap-3 items-start">
      <p className="text-gray-500 text-sm sm:text-base">
        Niciun tratament adăugat pentru acest pacient.
      </p>
      {onAddToPlan && (
        <Button variant="primary" onClick={onAddToPlan}>
          ➕ Adaugă la plan
        </Button>
      )}
    </div>
  );

  const renderFooter = () => (
    <div className="mt-6 flex flex-wrap justify-center md:justify-end gap-3">
      <Button
        variant="warning"
        onClick={handleExportProforma}
        disabled={allPlanServices.length === 0}
      >
        📑 Export plan tratament
      </Button>
      <Button variant="primary" onClick={handleExportPdf}>
        API
      </Button>
      <Button variant="success" onClick={handleExportQr}>
        🔗 QR Code
      </Button>
      <Button variant="secondary" onClick={handleSendEmail}>
        ✉️ Trimite Email
      </Button>
      {onFinancial && (
        <Button variant="info" onClick={onFinancial}>
          💰 Financiar
        </Button>
      )}
    </div>
  );

  const handleCollapseToggle = (toothId, isDone) => {
    if (!isDone) return;
    setCollapsedDone((prev) => ({
      ...prev,
      [toothId]: prev[toothId] === false,
    }));
  };

  const renderTooth = (t) => {
    const services = Array.isArray(t.services) ? t.services : [];
    const scope = normalizeScope(t.scope, t.tooth);
    const isDone = !!t.done;
    const completedServicesCount = services.filter((srv) => srv.done).length;
    const isCollapsed = isDone && collapsedDone[t.id] !== false;
    const containerClasses = `mb-6 rounded-xl shadow-sm border ${
      isDone ? "bg-gray-200" : "bg-white"
    }`;

    const entry = summaryById.get(t.id) || {
      paymentSummary: calculateToothPayment(services, t.payment),
      statusText: getPaymentStatusText(
        calculateToothPayment(services, t.payment)
      ),
    };

    const paymentInfo = entry.paymentSummary;
    let statusClass = "text-gray-500";
    switch (paymentInfo.status) {
      case "paid":
      case "advance":
        statusClass = "text-green-600";
        break;
      case "partial":
        statusClass = "text-orange-600";
        break;
      case "unpaid":
        statusClass = "text-red-600";
        break;
      default:
        statusClass = "text-gray-500";
    }
    const statusText = entry.statusText;

    // state pentru input sumă
    const paymentInput = paymentInputs[t.id] ?? "";
    const outstanding = Math.max(0, Number(entry.paymentSummary?.rest || 0));
    const availableAdvance = Number(effectivePatient?.advanceBalance || 0);

    const setPaymentInputValue = (value) => {
      setPaymentInputs((prev) => ({ ...prev, [t.id]: value }));
    };

    const parseAmount = () => {
      const raw = (paymentInput || "").replace(/\s+/g, "");
      if (!raw) return null;
      const value = Number(raw.replace(",", "."));
      if (!Number.isFinite(value) || value <= 0) return null;
      return value;
    };

    const handleSubmitPayment = async ({ method, useAdvance = false }) => {
      if (!patientId) return;
      const amount = parseAmount();
      if (!amount || amount <= 0) return;

      let effectiveAmount = amount;

      if (useAdvance) {
        if (availableAdvance <= 0) {
          alert("Nu există avans disponibil.");
          return;
        }
        if (outstanding <= 0) {
          alert("Nu există rest de plată pentru această zonă.");
          return;
        }
        const maxUsable = Math.min(outstanding, availableAdvance);
        if (!(maxUsable > 0)) {
          alert("Nu există sumă disponibilă pentru această zonă.");
          return;
        }
        effectiveAmount = Math.min(amount, maxUsable);
      }

      try {
        const label = getScopeLabel(scope, t.tooth).toLowerCase();
        const payload = {
          treatmentId: t.id,
          amount: effectiveAmount,
          method,
          useAdvance,
          description: useAdvance
            ? `Consum avans pentru ${label}`
            : `Plată ${method} pentru ${label}`,
        };
        const data = await registerPayment(patientId, payload);
        if (data?.patient) {
          syncPatient(data.patient);
        }
        setPaymentInputValue("");
      } catch (err) {
        console.error("Eroare procesare plată:", err);
        alert(err.message || "Nu am putut procesa plata.");
      }
    };

    return (
      <div key={t.id} className={containerClasses}>
        <div
          className={`flex flex-col md:flex-row md:items-start justify-between gap-3 px-4 py-3 border-b ${
            isDone ? "cursor-pointer" : ""
          }`}
          onClick={() => handleCollapseToggle(t.id, isDone)}
        >
          <div className="space-y-1">
            <div className="flex items-center gap-3">
              <ToggleBadge
                checked={isDone}
                onToggle={async () => {
                  try {
                    await toggleTratamentStatus(patientId, t.id);
                  } catch (err) {
                    console.error("Eroare actualizare status tratament:", err);
                  }
                }}
                stopPropagation
                label={
                  isDone
                    ? "Marchează tratamentul ca nelucrat"
                    : "Marchează tratamentul ca lucrat"
                }
              />
              <h4 className="font-semibold text-base sm:text-lg">
                {getScopeLabel(scope, t.tooth)}
              </h4>
              <span className="text-sm text-gray-500">
                {completedServicesCount} / {services.length} servicii finalizate
              </span>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {onEditTooth && (
              <Button
                variant="secondary"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onEditTooth(t);
                }}
              >
                ✏️ Editează tratamentul
              </Button>
            )}
            <Button
              variant="danger"
              size="sm"
              onClick={async (e) => {
                e.stopPropagation();
                try {
                  await deleteTratament(patientId, t.id);
                } catch (err) {
                  console.error("Eroare ștergere tratament:", err);
                }
              }}
            >
              🗑️ Șterge
            </Button>
          </div>
        </div>

        {/* SUMĂ ACHITATĂ + metode */}
        <div className="px-4 py-3 border-b bg-gray-50 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap items-center gap-3">
            <label className="text-sm font-medium text-gray-700">
              Sumă achitată (RON)
            </label>
            <input
              type="text"
              inputMode="decimal"
              value={paymentInput}
              onChange={(e) => setPaymentInputValue(e.target.value)}
              className="border rounded px-2 py-1 text-sm w-28"
              onClick={(e) => e.stopPropagation()}
              placeholder="ex: 250"
            />
            <div className="flex gap-2">
              <button
                className="px-3 py-1 rounded text-sm border bg-green-500 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!parseAmount()}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSubmitPayment({ method: "cash" });
                }}
              >
                Cash
              </button>
              <button
                className="px-3 py-1 rounded text-sm border bg-blue-500 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!parseAmount()}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSubmitPayment({ method: "card" });
                }}
              >
                Card
              </button>
              <button
                className="px-3 py-1 rounded text-sm border bg-emerald-500 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!parseAmount() || availableAdvance <= 0 || outstanding <= 0}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSubmitPayment({ method: "advance", useAdvance: true });
                }}
              >
                Avans
              </button>
            </div>
          </div>
          <div className="text-sm text-gray-600 space-y-1">
            <div className={statusClass}>{statusText}</div>
            <div className="flex flex-wrap gap-3 text-xs sm:text-sm text-gray-500">
              <span>
                Rest de plată:{" "}
                <strong className="text-gray-700">
                  {formatCurrencyValue(outstanding)} RON
                </strong>
              </span>
              <span>
                Avans disponibil pacient:{" "}
                <strong className="text-gray-700">
                  {formatCurrencyValue(availableAdvance)} RON
                </strong>
              </span>
            </div>
          </div>
        </div>

        {(!isDone || !isCollapsed) && (
          <div className="overflow-x-auto">
            <table className={`w-full text-xs sm:text-sm ${isDone ? "opacity-80" : ""}`}>
              <thead className="bg-gray-200 text-gray-700">
                <tr>
                  <th className="p-2 text-left">Finalizat</th>
                  <th className="p-2 text-left">Serviciu</th>
                  <th className="p-2 text-left">Preț listă</th>
                  <th className="p-2 text-left">Discount (%)</th>
                  <th className="p-2 text-left">Preț final</th>
                  <th className="p-2 text-left">Acțiuni</th>
                </tr>
              </thead>
              <tbody>
                {services.map((srv, i) => {
                  const handleServiceUpdate = async (changes) => {
                    const updatedService = { ...srv, ...changes };
                    const nextServices = services.map((item, idx) =>
                      idx === i ? updatedService : item
                    );
                    try {
                      await updateTratament(patientId, t.id, {
                        tooth: t.tooth,
                        services: nextServices,
                      });
                    } catch (err) {
                      console.error("Eroare actualizare serviciu tratament:", err);
                    }
                  };

                  const handleToggle = async () => {
                    try {
                      await toggleTratamentServiceStatus(patientId, t.id, i);
                    } catch (err) {
                      console.error("Eroare actualizare status serviciu:", err);
                    }
                  };

                  const handleDelete = async () => {
                    try {
                      await deleteTratamentService(patientId, t.id, i);
                    } catch (err) {
                      console.error("Eroare ștergere serviciu tratament:", err);
                    }
                  };

                  return (
                    <TreatmentServiceRow
                      key={srv.id || `${t.id}-${i}`}
                      service={srv}
                      formatCurrencyValue={formatCurrencyValue}
                      onToggle={handleToggle}
                      onDelete={handleDelete}
                      onSave={handleServiceUpdate}
                      ToggleComponent={ToggleBadge}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="bg-gray-100 rounded-2xl shadow p-4 sm:p-6 mt-6">
      {pacientTratamente.length === 0 ? (
        renderEmpty()
      ) : (
        <>
          {pacientTratamente.map(renderTooth)}

          <div className="text-right text-sm sm:text-base space-y-1">
            <p className="font-semibold">Total general: {formatCurrencyValue(totalGeneral)} RON</p>
            <p>Achitat: {formatCurrencyValue(totalPaidAmount)} RON</p>
            <p className="font-bold text-lg">
              Rest de plată: {formatCurrencyValue(totalRemaining)} RON
            </p>
            {totalAdvance > 0 && (
              <p className="text-green-600">
                Avans: {formatCurrencyValue(totalAdvance)} RON
              </p>
            )}
            {patientAdvanceBalance > 0 && (
              <p className="text-green-600">
                Avans disponibil pacient: {formatCurrencyValue(patientAdvanceBalance)} RON
              </p>
            )}
          </div>

          {renderFooter()}
        </>
      )}
    </div>
  );
}
